import { useMemo, useRef, useSyncExternalStore } from 'react';
import { AuthController, type AuthControllerState } from './AuthController';

/**
 * useAuthController — реактивный доступ к авторизации с экшенами.
 * По умолчанию возвращает полный объект (state + actions).
 * Если передан селектор — вернёт выбранный фрагмент из полного объекта.
 */
export function useAuthController(): ReturnType<typeof withActions>;
export function useAuthController<S>(selector: (full: ReturnType<typeof withActions>) => S): S;
export function useAuthController<S = ReturnType<typeof withActions>>(
  selector?: (full: ReturnType<typeof withActions>) => S
): S | ReturnType<typeof withActions> {
  const ctrl = useMemo(() => AuthController.getInstance(), []);
  const snapshot = useSyncExternalStore(ctrl.subscribe, ctrl.getSnapshot, ctrl.getSnapshot);

  const full = useMemo(() => withActions(ctrl, snapshot), [ctrl, snapshot]);

  if (!selector) return full;

  const selectedRef = useRef<S | undefined>(undefined);
  return useMemo(() => {
    const selected = selector(full);
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        /* noop */
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [full, selector]);
}

/**
 * Добавляет экшены контроллера к снапшоту состояния.
 */
function withActions(controller: AuthController, snapshot: AuthControllerState) {
  return {
    // state
    isAuthenticated: snapshot.isAuthenticated,
    user: snapshot.user,
    isLoading: snapshot.isLoading,
    error: snapshot.error,
    // actions
    login: controller.login.bind(controller),
    logout: controller.logout.bind(controller),
    register: controller.register.bind(controller),
    changePassword: controller.changePassword.bind(controller),
    clearError: controller.clearError.bind(controller),
    hasPermission: controller.hasPermission.bind(controller),
  };
}
